import type { GridColDef } from '@mui/x-data-grid';
import type { GetContractsOverviewsResponse } from '@weft/api-definition';
import { CONTRACT_STATUSES } from '@weft/types';
import { dayjs, isNotNullOrUndefined, type ItemOf } from '@weft/utils';
import { ContractStatusChip } from 'apps/app/src/components/design/ContractStatusChip';
import { formatDate } from 'apps/app/src/utils/date';
import type { FieldPath } from 'react-hook-form';
import { get } from 'react-hook-form';
import { InvoiceSyncStatusLabel } from '../../../invoices/InvoicesPage/invoices-page-components/InvoiceSyncStatusLabel/InvoiceSyncStatusLabel';
import { getPlanDurationLabel } from '../../CreatePlanPage/create-plan-page-components/PlanDurationAutoComplete';
import { ContractDotMenu } from '../plans-page-components/ContractDotMenu';

export const CONTRACTS_COLUMNS_KEYS = Object.freeze({
  STATUS: 'status',
  CUSTOMER_NAME: 'customer.name',
  REVENUE: 'revenue',
  PLAN_NAME: 'plan.name',
  START_DATE: 'startDate',
  PLAN_DURATION: 'plan.billingData.duration',
  PLAN_TYPE: 'plan.billingData.paymentTerm',
  CREATED_AT: 'createdAt',
  SYNC_STATUS: 'syncStatus',
} satisfies Record<string, FieldPath<ItemOf<GetContractsOverviewsResponse['contracts']>>>);

export const CONTRACTS_COLUMNS: GridColDef<ItemOf<GetContractsOverviewsResponse['contracts']>>[] = [
  {
    field: CONTRACTS_COLUMNS_KEYS.STATUS,
    headerName: 'Status',
    flex: 1,
    headerClassName: 'pl-7',
    cellClassName: 'pl-6',
    renderCell: ({ row }) => (
      <ContractStatusChip
        status={row.status}
        endDate={row.endDate}
      />
    ),
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.CUSTOMER_NAME,
    valueGetter: ({ row, field }) => get(row, field),
    headerName: 'Customer',
    flex: 1,
    renderCell: ({ value }) => <span className="font-sans text-black">{value}</span>,
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.PLAN_NAME,
    valueGetter: ({ row, field }) => get(row, field),
    headerName: 'Contract',
    flex: 1,
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.START_DATE,
    valueGetter: ({ row, field }) => get(row, field),
    headerName: 'Start date',
    flex: 1,
    renderCell: ({ row }) => <span className="font-sans text-black">{formatDate(row.startDate)}</span>,
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.PLAN_DURATION,
    valueGetter: ({ row, field }) => get(row, field),
    headerName: 'Duration',
    flex: 1,
    renderCell: ({ value }) => <span className="font-sans text-black">{getPlanDurationLabel(value)}</span>,
  },
  {
    field: CONTRACTS_COLUMNS_KEYS.SYNC_STATUS,
    headerName: 'SO Sync',
    headerAlign: 'center',
    flex: 1,
    renderCell: (params) => {
      const syncStatus = params.row.syncStatus;
      const syncStatusUpdatedAt = params.row.syncStatusUpdatedAt;

      if (isNotNullOrUndefined(syncStatus) && isNotNullOrUndefined(syncStatusUpdatedAt)) {
        const syncStatusUpdatedAtDate = formatDate(dayjs(syncStatusUpdatedAt), 'MMM. D, YY HH:mm z');

        return (
          <InvoiceSyncStatusLabel
            className='flex w-full items-center justify-center'
            syncStatus={syncStatus}
            syncStatusUpdatedAt={syncStatusUpdatedAtDate}
          />
        );
      }

      return <span />;
    },
  },
  {
    field: 'contractMenu',
    headerName: '',
    renderCell: ({ row: contract }) => (
      <ContractDotMenu
        contractId={contract.id}
        planId={contract.plan.id}
        planName={contract.plan.name}
        billingData={contract.plan.billingData}
        customerName={contract.customer.name}
        isActive={contract.status === CONTRACT_STATUSES.Active}
        isTerminated={contract.status === CONTRACT_STATUSES.Terminated || contract.status === CONTRACT_STATUSES.PendingTermination}
        contractStartDate={contract.startDate}
        contractEndDate={contract.endDate}
        syncStatus={contract.syncStatus}
      />
    ),
  },
];